
import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableVehicleItemProps {
    vehicle: any;
    index: number;
}

/**
 * ソート可能なリストアイテム
 */
function SortableVehicleItem({ vehicle, index }: SortableVehicleItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: vehicle.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                backgroundColor: isDragging ? '#f5f5f5' : '#fff',
                cursor: 'grab',
                '&:active': {
                    cursor: 'grabbing',
                },
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 2,
                    color: 'text.secondary',
                }}
            >
                <DragIndicatorIcon />
            </Box>
            <Chip
                label={index + 1}
                size="small"
                color="primary"
                sx={{ mr: 2, minWidth: 40 }}
            />
            <ListItemText
                primary={vehicle.vehicle_code}
                secondary={`現在の優先度: ${vehicle.priority !== null && vehicle.priority !== undefined ? vehicle.priority : 'なし'}`}
            />
        </ListItem>
    );
}

interface SortableVehicleTypeItemProps {
    vehicleType: any;
    index: number;
}

/**
 * ソート可能な車両タイプアイテム
 */
function SortableVehicleTypeItem({ vehicleType, index }: SortableVehicleTypeItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: vehicleType.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                backgroundColor: isDragging ? '#f5f5f5' : '#fff',
                cursor: 'grab',
                '&:active': {
                    cursor: 'grabbing',
                },
            }}
        >
            <Box
                {...attributes}
                {...listeners}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mr: 2,
                    color: 'text.secondary',
                }}
            >
                <DragIndicatorIcon />
            </Box>
            <Chip
                label={index + 1}
                size="small"
                color="primary"
                sx={{ mr: 2, minWidth: 40 }}
            />
            <ListItemText
                primary={vehicleType.name}
                secondary={`現在の優先度: ${vehicleType.priority !== null && vehicleType.priority !== undefined ? vehicleType.priority : 'なし'}`}
            />
        </ListItem>
    );
}

interface VehicleOrderDialogProps {
    open: boolean;
    onClose: () => void;
    vehicleTypes: any[];
    vehicles: any[];
    onSave: (updates: any[]) => Promise<void>;
    onSaveVehicleTypes: (updates: any[]) => Promise<void>;
}

/**
 * 車両順番編集ダイアログ
 */
export default function VehicleOrderDialog({
    open,
    onClose,
    vehicleTypes,
    vehicles,
    onSave,
    onSaveVehicleTypes, // 車両タイプ保存用
}: VehicleOrderDialogProps) {
    const [mode, setMode] = useState<'vehicle' | 'vehicleType'>('vehicle');
    const [selectedTypeId, setSelectedTypeId] = useState<any>(null);
    const [orderedVehicles, setOrderedVehicles] = useState<any[]>([]);
    const [orderedVehicleTypes, setOrderedVehicleTypes] = useState<any[]>([]);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 車両タイプを選択
    const handleSelectType = (typeId: any) => {
        setSelectedTypeId(typeId);
        setError(null);

        // 選択された車両タイプの車両を取得
        // out_of_useがtrueの車両は除外し、優先度順にソート
        const typeVehicles = vehicles
            .filter((v: any) => v.vehicle_type?.id === typeId && v.out_of_use !== true)
            .sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));

        setOrderedVehicles(typeVehicles);
    };

    // ドラッグ終了時の処理
    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedVehicles((items) => {
                const oldIndex = items.findIndex((item: any) => item.id === active.id);
                const newIndex = items.findIndex((item: any) => item.id === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    // 保存処理
    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            // 選択された車両タイプのout_of_use車両を取得
            const outOfUseVehicles = vehicles.filter(
                (v: any) => v.vehicle_type?.id === selectedTypeId && v.out_of_use === true
            );

            // アクティブな車両の新しい優先度を計算（上から順に大きい値）
            const activeUpdates = orderedVehicles.map((vehicle: any, index: number) => ({
                id: vehicle.id,
                priority: orderedVehicles.length + outOfUseVehicles.length - index,
            }));

            // out_of_use車両は最低優先度に設定
            const outOfUseUpdates = outOfUseVehicles.map((vehicle: any, index: number) => ({
                id: vehicle.id,
                priority: outOfUseVehicles.length - index, // 1, 2, 3... (最低)
            }));

            // 両方を結合して保存
            const allUpdates = [...activeUpdates, ...outOfUseUpdates];

            await onSave(allUpdates);

            // 成功したら閉じる
            handleClose();
        } catch (err) {
            console.error('Failed to save vehicle order:', err);
            setError('保存に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    // ダイアログを閉じる
    const handleClose = () => {
        setMode('vehicle');
        setSelectedTypeId(null);
        setOrderedVehicles([]);
        setOrderedVehicleTypes([]);
        setError(null);
        onClose();
    };

    // モード切り替え
    const handleModeChange = (newMode: 'vehicle' | 'vehicleType') => {
        setMode(newMode);
        setSelectedTypeId(null);
        setOrderedVehicles([]);
        setError(null);

        if (newMode === 'vehicleType') {
            // 車両タイプを優先度順にソート
            const sorted = [...vehicleTypes].sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0));
            setOrderedVehicleTypes(sorted);
        }
    };

    // 車両タイプのドラッグ終了時の処理
    const handleVehicleTypeDragEnd = (event: any) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedVehicleTypes((items) => {
                const oldIndex = items.findIndex((item: any) => item.id === active.id);
                const newIndex = items.findIndex((item: any) => item.id === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    // 車両タイプの保存処理
    const handleSaveVehicleTypes = async () => {
        setSaving(true);
        setError(null);

        try {
            // 新しい優先度を計算（上から順に大きい値）
            const updates = orderedVehicleTypes.map((type: any, index: number) => ({
                id: type.id,
                priority: orderedVehicleTypes.length - index,
            }));

            await onSaveVehicleTypes(updates);

            // 成功したら閉じる
            handleClose();
        } catch (err) {
            console.error('Failed to save vehicle type order:', err);
            setError('保存に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle>
                車両順番編集
                {selectedTypeId && mode === 'vehicle' && (
                    <Button
                        size="small"
                        onClick={() => {
                            setSelectedTypeId(null);
                            setOrderedVehicles([]);
                        }}
                        sx={{ ml: 2 }}
                    >
                        ← 車両タイプ選択に戻る
                    </Button>
                )}
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* タブナビゲーション */}
                {!selectedTypeId && (
                    <Tabs
                        value={mode}
                        onChange={(e: any, newValue: any) => handleModeChange(newValue)}
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab label="車両の順番" value="vehicle" />
                        <Tab label="車両タイプの順番" value="vehicleType" />
                    </Tabs>
                )}

                {mode === 'vehicleType' ? (
                    // 車両タイプ並び替え画面
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            ドラッグ&ドロップで順番を入れ替えてください（上が優先度高）
                        </Typography>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleVehicleTypeDragEnd}
                        >
                            <SortableContext
                                items={orderedVehicleTypes.map((t: any) => t.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <List>
                                    {orderedVehicleTypes.map((type: any, index: number) => (
                                        <SortableVehicleTypeItem
                                            key={type.id}
                                            vehicleType={type}
                                            index={index}
                                        />
                                    ))}
                                </List>
                            </SortableContext>
                        </DndContext>
                    </Box>
                ) : !selectedTypeId ? (
                    // 車両タイプ選択画面
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            編集する車両タイプを選択してください
                        </Typography>
                        <List>
                            {vehicleTypes.map((type: any) => {
                                const typeVehicleCount = vehicles.filter(
                                    (v: any) => v.vehicle_type?.id === type.id && v.out_of_use !== true
                                ).length;

                                return (
                                    <ListItem
                                        key={type.id}
                                        {...{button: true} as any}
                                        onClick={() => handleSelectType(type.id)}
                                        sx={{
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            mb: 1,
                                            '&:hover': {
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={type.name}
                                            secondary={`${typeVehicleCount}台（稼働中）`}
                                        />
                                        <Chip
                                            label={`優先度: ${type.priority}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                ) : (
                    // 車両並び替え画面
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            ドラッグ&ドロップで順番を入れ替えてください（上が優先度高）
                        </Typography>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={orderedVehicles.map((v: any) => v.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <List>
                                    {orderedVehicles.map((vehicle: any, index: number) => (
                                        <SortableVehicleItem
                                            key={vehicle.id}
                                            vehicle={vehicle}
                                            index={index}
                                        />
                                    ))}
                                </List>
                            </SortableContext>
                        </DndContext>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    キャンセル
                </Button>
                {mode === 'vehicleType' ? (
                    <Button
                        onClick={handleSaveVehicleTypes}
                        variant="contained"
                        disabled={saving || orderedVehicleTypes.length === 0}
                        startIcon={saving && <CircularProgress size={16} />}
                    >
                        {saving ? '保存中...' : '保存'}
                    </Button>
                ) : selectedTypeId && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || orderedVehicles.length === 0}
                        startIcon={saving && <CircularProgress size={16} />}
                    >
                        {saving ? '保存中...' : '保存'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
